import { captureException } from "@sentry/nextjs";
import {
  Divider,
  Input,
  Spin,
  Typography,
  Row,
  Col,
  Form as AntdForm,
} from "antd";
import dynamic from "next/dynamic";
import Link from "next/link";
import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useCookies } from "react-cookie";
import { useIntl, defineMessages, FormattedMessage } from "react-intl";

import { rtfValues } from "../../../lib/formats/common";
import { SignUpMutation, UserError } from "../../../lib/graphql";
import { useCurrentLocale } from "../../../lib/hooks/basic/locales";
import { useCurrentUser, useSignUpUser } from "../../../lib/hooks/basic/users";
import {
  captureSignupEvent,
  UTM_COOKIE_NAMES,
} from "../../../lib/posthog/acquisition";
import { hardRedirect } from "../../../lib/redirect";
import routes from "../../../lib/routes";
import {
  Form,
  type FormProps,
  Rules,
  RuleBuilder,
  useFormContext,
} from "../../form/Form";
import { SubmitButton } from "../../form/SubmitButton";
import { CountrySelector } from "../../shared/Address/CountrySelector";
import { Space } from "../../shared/Space";
import { useTurnstile } from "./useTurnstile";

const PhoneInput = dynamic(() => import("antd-phone-input"), {
  ssr: false,
  loading: () => <Spin style={{ marginBottom: 24 }} />,
});

const messages = defineMessages({
  accountTitle: {
    id: "user.signUp.completeInfo",
    defaultMessage: "Completa la siguiente información",
  },
  accountSubtitle: {
    id: "user.signUp.subtitle",
    defaultMessage:
      "You're one step away from digitizing your farm. Create your <strong>{planName}</strong> account and start logging your activities in minutes.",
  },
  submitPaid: {
    id: "user.signUp.submitPaid",
    defaultMessage: "Start now!",
  },
  creating: {
    id: "user.signUp.creating",
    defaultMessage: "Creating your account...",
  },
  firstName: { id: "user.yourFirstName", defaultMessage: "Your First Name" },
  lastName: { id: "user.yourLastName", defaultMessage: "Your Last Name" },
  email: { id: "users.yourEmail", defaultMessage: "Your email" },
  password: { id: "user.yourPassword", defaultMessage: "Create your password" },
  passwordMinLength: {
    id: "user.passwordMinLength",
    defaultMessage: "Password must be at least 8 characters",
  },
  captchaError: {
    id: "form.validation.captcha",
    defaultMessage: "Please complete the captcha",
  },
  companyName: {
    id: "user.companyNamePlaceholder",
    defaultMessage: "Company name (legal name)",
  },
  country: {
    id: "user.countryPlaceholder",
    defaultMessage: "Where is your farm located?",
  },
  phoneNumber: {
    id: "user.phoneNumberPlaceholder",
    defaultMessage: "Your mobile number",
  },
  phoneNumberCountry: {
    id: "user.phoneNumberCountryPlaceholder",
    defaultMessage: "Search country",
  },
});

export type OnSaveProp<T> = (props: {
  variables: T;
  setCreating: (creating: boolean) => void;
  setSubmitting: (submitting: boolean) => void;
  showErrors: (errors: UserError[]) => void;
  showSuccess: (message: string) => void;
}) => Promise<SignUpMutation | undefined>;

export type NewAccountFormProps = {
  token?: string | null;
  tenantName?: string | null;
  plan?: string | null;
  period?: string | null;
  countryCode?: string | null;
  onSuccess?: (result: SignUpMutation["signUp"]) => void;
};

export default function SignUpForm({
  token,
  tenantName,
  plan,
  period,
  countryCode,
  onSuccess,
}: NewAccountFormProps) {
  const intl = useIntl();
  const { user } = useCurrentUser();
  const [creating, setCreating] = useState(!!user);
  const { currentLocale } = useCurrentLocale();
  const localeCode = currentLocale || "es";
  const showEmail = !token;
  const [cookies] = useCookies(UTM_COOKIE_NAMES);
  const utmCookies = useRef(cookies);
  const hasCapturedView = useRef(false);

  const planName = intl.formatMessage({
    id: `subscriptionPlans.${plan || "free"}.name`,
  });

  const [save] = useSignUpUser();
  const {
    token: captchaToken,
    reset: resetCaptcha,
    containerRef: captchaContainerRef,
    ScriptTag: CaptchaScript,
  } = useTurnstile(localeCode);

  const initialValues = useMemo(
    () => ({
      email: "",
      firstName: "",
      lastName: "",
      name: tenantName || "",
      countryCode,
      localeCode,
      password: "",
      captchaResponse: "",
    }),
    [localeCode, countryCode, tenantName]
  );

  const trackingContext = useMemo(
    () => ({
      form: "full" as const,
      plan,
      period,
      token,
      countryCode,
    }),
    [countryCode, period, plan, token]
  );

  useEffect(() => {
    if (hasCapturedView.current) return;
    hasCapturedView.current = true;
    captureSignupEvent("signup_viewed", trackingContext, utmCookies.current);
  }, [trackingContext]);

  const onSubmit = useCallback<
    NonNullable<FormProps<typeof initialValues>["onSubmit"]>
  >(
    (variables, { setSubmitting, showErrors }) => {
      setSubmitting(true);
      const resolvedCountryCode = variables.countryCode || "NI";
      const submissionContext = {
        ...trackingContext,
        countryCode: resolvedCountryCode,
      };
      captureSignupEvent("signup_submitted", submissionContext, cookies);

      const showCaptchaError = () => {
        showErrors([
          {
            path: "captchaResponse",
            message: intl.formatMessage(messages.captchaError),
          },
        ]);
      };

      if (!captchaToken) {
        captureSignupEvent("signup_failed", submissionContext, cookies, {
          reason: "captcha_missing",
        });
        resetCaptcha();
        showCaptchaError();
        setSubmitting(false);
        return;
      }

      if (typeof window === "undefined" || !window.turnstile) {
        captureSignupEvent("signup_failed", submissionContext, cookies, {
          reason: "captcha_unavailable",
        });
        captureException(new Error("Captcha not available on signup form"));
        showCaptchaError();
        resetCaptcha();
        setSubmitting(false);
        return;
      }

      variables.captchaResponse = captchaToken;

      let requestFailed = false;

      save({
        variables: {
          ...variables,
          invitationToken: token,
          countryCode: resolvedCountryCode,
          tenant: variables.name || tenantName,
          plan,
          period,
        },
      })
        .catch((e) => {
          requestFailed = true;
          captureSignupEvent("signup_failed", submissionContext, cookies, {
            reason: "network_error",
          });
          captureException(e);
          resetCaptcha();
          setSubmitting(false);
        })
        .then((result) => {
          if (requestFailed) return;

          if (!result || !result.data || !result.data.signUp) {
            captureSignupEvent("signup_failed", submissionContext, cookies, {
              reason: "empty_response",
            });
            resetCaptcha();
            setSubmitting(false);
            return;
          }

          if (result.data.signUp.errors) {
            captureSignupEvent("signup_failed", submissionContext, cookies, {
              reason: "server_errors",
            });
            resetCaptcha();
            showErrors(result.data.signUp.errors || []);
            setSubmitting(false);
            return;
          }

          captureSignupEvent("signup_succeeded", submissionContext, cookies);
          onSuccess?.(result.data.signUp);
          setCreating(true);
          localStorage.clear();

          hardRedirect(
            result.data.signUp.redirectTo || routes.onboarding + "?pillars=true"
          );
        });
    },
    [
      captchaToken,
      cookies,
      intl,
      onSuccess,
      resetCaptcha,
      save,
      trackingContext,
      period,
      plan,
      tenantName,
      token,
    ]
  );

  return (
    <Space vertical size="large">
      <div>
        <Typography.Title level={3} className="auth-form-copy">
          {intl.formatMessage(messages.accountTitle)}
        </Typography.Title>

        <Typography.Text className="auth-form-copy" style={{ fontSize: 16 }}>
          {intl.formatMessage(messages.accountSubtitle, {
            planName,
            ...rtfValues,
          })}
        </Typography.Text>
      </div>

      <Form initialValues={initialValues} onSubmit={onSubmit}>
        {creating ? (
          <Space vertical align="center">
            <Spin description={intl.formatMessage(messages.creating)} />
          </Space>
        ) : (
          <>
            {CaptchaScript}
            <AccountStep
              showEmail={showEmail}
              captchaContainerRef={captchaContainerRef}
            />

            <Space vertical size="middle" align="center">
              <SubmitButton
                size="large"
                className="new-primary"
                style={{ minWidth: "180px" }}
                data-attr="signup/submit-btn"
              >
                {intl.formatMessage(messages.submitPaid)}
              </SubmitButton>

              <AcceptTermsAndPolicyFormItem locale={localeCode} />
            </Space>

            <Divider style={{ margin: "30px 0" }}>
              <Typography.Text
                type="secondary"
                style={{
                  textTransform: "uppercase",
                  opacity: 0.7,
                  fontWeight: 400,
                }}
              >
                <FormattedMessage id="or" defaultMessage="Or" />
              </Typography.Text>
            </Divider>

            <div style={{ textAlign: "center", paddingBottom: 20 }}>
              <Link href={routes.user.login}>
                <Typography.Text type="secondary">
                  <FormattedMessage id="signIn" defaultMessage="Sign In" />
                </Typography.Text>
              </Link>
            </div>
          </>
        )}
      </Form>
    </Space>
  );
}

function AccountStep({
  showEmail,
  captchaContainerRef,
}: {
  showEmail: boolean;
  captchaContainerRef: React.RefObject<HTMLDivElement>;
}) {
  const intl = useIntl();
  const { form } = useFormContext();
  const countryCodeValue = AntdForm.useWatch("countryCode", form as any);
  const phoneCountry = countryCodeValue?.toLowerCase();

  return (
    <>
      <Row gutter={16}>
        <Col span={24} md={12}>
          <Form.Item
            name="firstName"
            rules={[Rules.required, Rules.name, RuleBuilder.maxLength(100)]}
          >
            <Input
              autoFocus
              placeholder={intl.formatMessage(messages.firstName)}
              className="new-input"
              autoComplete="given-name"
            />
          </Form.Item>
        </Col>

        <Col span={24} md={12}>
          <Form.Item
            name="lastName"
            rules={[Rules.required, Rules.name, RuleBuilder.maxLength(100)]}
          >
            <Input
              placeholder={intl.formatMessage(messages.lastName)}
              className="new-input"
              autoComplete="family-name"
            />
          </Form.Item>
        </Col>
      </Row>

      {showEmail && (
        <Form.Item
          name="email"
          rules={[Rules.required, Rules.email]}
          validateTrigger="onBlur"
        >
          <Input
            placeholder={intl.formatMessage(messages.email)}
            className="new-input"
          />
        </Form.Item>
      )}

      <Form.Item
        name="name"
        rules={[Rules.required, RuleBuilder.maxLength(50)]}
      >
        <Input
          placeholder={intl.formatMessage(messages.companyName)}
          className="new-input"
        />
      </Form.Item>

      <Form.Item name="countryCode" rules={[Rules.required]}>
        <CountrySelector
          style={{ width: "100%", textAlign: "left" }}
          placeholder={intl.formatMessage(messages.country)}
          className="new-input"
          allowClear={false}
        />
      </Form.Item>

      <Form.Item
        name="password"
        rules={[
          Rules.required,
          { min: 8, message: intl.formatMessage(messages.passwordMinLength) },
        ]}
      >
        <Input.Password
          placeholder={intl.formatMessage(messages.password)}
          className="new-input"
        />
      </Form.Item>

      <PhoneInput
        key={phoneCountry}
        distinct
        enableSearch
        disableParentheses
        className="new-input"
        searchPlaceholder={intl.formatMessage(messages.phoneNumberCountry)}
        country={phoneCountry}
        onChange={(value) => {
          if (!value.phoneNumber) {
            form.setFieldsValue({
              phoneNumber: null,
            });
            return;
          }
          form.setFieldsValue({
            phoneNumber: `+${value.countryCode}${value.areaCode}${value.phoneNumber}`,
          });

          if (!form.getFieldValue("countryCode")) {
            form.setFieldsValue({
              countryCode: value.isoCode?.toUpperCase(),
            });
          }
        }}
        style={{ marginBottom: 24 }}
      />

      <div style={{ textAlign: "center" }}>
        <Form.Item name="captchaResponse" noStyle>
          <div ref={captchaContainerRef} style={{ marginBottom: 16 }} />
        </Form.Item>
      </div>
    </>
  );
}

export function AcceptTermsAndPolicyFormItem({ locale }: { locale: string }) {
  return (
    <div style={{ textAlign: "center" }}>
      <FormattedMessage
        id="users.acceptTermsAndPolicies"
        defaultMessage="By signing up you accept our <terms>Terms and Conditions</terms> and <policies>Privacy Policy</policies>"
        values={{
          terms: (txt) => (
            <a
              target="_blank"
              rel="noreferrer"
              href={`https://aragro.com/${locale}/terms`}
            >
              {txt}
            </a>
          ),
          policies: (txt) => (
            <a
              target="_blank"
              rel="noreferrer"
              href={`https://aragro.com/${locale}/privacy`}
            >
              {txt}
            </a>
          ),
        }}
      />
    </div>
  );
}
